/*
  Donau: Header klappt beim Runterscrollen zur schmalen Leiste ein.
  - Kompakt: kleineres Logo, Desktop-Menü weg, an seiner Stelle der Knopf „Menü“.
  - Klick auf den Knopf (oder die freie Fläche der Leiste) klappt wieder auf;
    ganz oben (scrollY <= 10) wird automatisch wieder voll angezeigt.
  - Die Höhe der Header-Sektion wird beim Einklappen festgehalten („locked“): Die Seite
    darunter verschiebt sich dadurch nie – kein Springen, kein Hin-und-Her, kein Layout-Shift.
    Die frei werdende Fläche ist durchsichtig und lässt Klicks durch.
*/
(() => {
  if (customElements.get('donau-compact-header')) return;

  const TOP = 10; // bis hierhin immer voll
  const DOWN = 8; // so viele px am Stück nach unten, bevor eingeklappt wird
  const REARM = 200; // nach manuellem Aufklappen erst nach so vielen px wieder einklappen
  const EXTRA = 120; // zusätzlicher Abstand unter dem Header, bevor zum ersten Mal eingeklappt wird

  class DonauCompactHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.section-header');
      this.wrapper = this.closest('.header-wrapper');
      if (!this.section || !this.wrapper) return;

      this.desktop = window.matchMedia('(min-width: 990px)');
      this.lastY = Math.max(window.scrollY, 0);
      this.lastW = window.innerWidth;
      this.down = 0;
      this.anchor = null;
      this.queued = false;

      this.onScroll = () => {
        if (this.queued) return;
        this.queued = true;
        requestAnimationFrame(() => {
          this.queued = false;
          this.update();
        });
      };
      // Mobil feuert „resize“ bei jedem Ein-/Ausblenden der Adressleiste – nur echte Breitenänderungen zählen.
      this.onResize = () => {
        if (window.innerWidth === this.lastW) return;
        this.lastW = window.innerWidth;
        this.reset();
      };
      this.onButton = () => this.expand(true);
      this.onBar = (event) => {
        if (!this.isCompact() || !this.desktop.matches) return;
        if (event.target.closest('a, button, summary, input, details, cart-notification')) return;
        this.expand(false);
      };
      // Der Hamburger-Drawer misst die (festgehaltene) Sektionshöhe – an die sichtbare Leiste anpassen.
      this.onDrawer = () => {
        setTimeout(() => {
          if (!this.isLocked()) return;
          const bottom = Math.round(this.wrapper.getBoundingClientRect().bottom);
          document.documentElement.style.setProperty('--header-bottom-position', `${bottom}px`);
        });
      };

      this.button = this.querySelector('button');
      this.button?.addEventListener('click', this.onButton);
      this.wrapper.addEventListener('click', this.onBar);
      this.drawerSummary = this.wrapper.querySelector('header-drawer summary');
      this.drawerSummary?.addEventListener('click', this.onDrawer);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.button?.removeEventListener('click', this.onButton);
      this.wrapper?.removeEventListener('click', this.onBar);
      this.drawerSummary?.removeEventListener('click', this.onDrawer);
      if (this.section) this.reset();
    }

    isCompact() {
      return this.section.classList.contains('donau-header--compact');
    }

    isLocked() {
      return this.section.classList.contains('donau-header--locked');
    }

    // Nicht einklappen, solange Drawer, Mega-Menü, Suche oder ein Modal offen ist
    // oder jemand per Tastatur im Menü unterwegs ist.
    isBusy() {
      return (
        this.section.classList.contains('menu-open') ||
        document.body.classList.contains('overflow-hidden') ||
        !!this.wrapper.querySelector('details[open]') ||
        !!this.wrapper.querySelector('.header__inline-menu :focus-visible')
      );
    }

    update() {
      const y = Math.max(window.scrollY, 0);
      const dy = y - this.lastY;
      this.lastY = y;

      if (y <= TOP) {
        this.reset();
        return;
      }
      if (dy < 0) {
        this.down = 0;
        if (this.anchor !== null) this.anchor = Math.min(this.anchor, y);
        return;
      }
      this.down += dy;
      if (this.isCompact() || this.isBusy() || this.down < DOWN) return;

      const farEnough =
        this.anchor !== null ? y - this.anchor >= REARM : y > this.section.offsetHeight + EXTRA;
      if (farEnough) this.compact();
    }

    compact() {
      if (!this.isLocked()) {
        this.section.style.setProperty('--donau-header-h', `${this.section.offsetHeight}px`);
        this.section.classList.add('donau-header--locked');
      }
      this.section.classList.add('donau-header--compact');
      this.anchor = null;
    }

    expand(moveFocus) {
      // bleibt „locked“ – die volle Leiste passt genau in die festgehaltene Höhe, nichts darunter bewegt sich
      this.section.classList.remove('donau-header--compact');
      this.anchor = Math.max(window.scrollY, 0);
      this.down = 0;
      if (moveFocus) {
        this.wrapper.querySelector('.header__inline-menu summary, .header__inline-menu a')?.focus();
      }
    }

    reset() {
      this.section.classList.remove('donau-header--compact', 'donau-header--locked');
      this.section.style.removeProperty('--donau-header-h');
      this.anchor = null;
      this.down = 0;
    }
  }

  customElements.define('donau-compact-header', DonauCompactHeader);
})();
